import type { RideProgressPlan } from "./ride";

const LOCAL_HEADING_RADIUS_KILOMETERS = 0.45;
const HEADING_SMOOTHING_RADIUS_KILOMETERS = 1.5;
const HEADING_SAMPLE_OFFSETS = [-1, -2 / 3, -1 / 3, 0, 1 / 3, 2 / 3, 1] as const;
const HEADING_SAMPLE_WEIGHTS = [1, 2, 3, 4, 3, 2, 1] as const;

/** Computes a route heading from a broad local interval to suppress GPS jitter. */
function routeHeading(route: RideProgressPlan, fraction: number, reference = fraction, localRadius = LOCAL_HEADING_RADIUS_KILOMETERS): number {
  const radius = headingFractionRadius(route, localRadius, localRadius === LOCAL_HEADING_RADIUS_KILOMETERS ? 0.003 : 0, 0.04);
  const start = route.pointNear(Math.max(0, fraction - radius), reference);
  const finish = route.pointNear(Math.min(1, fraction + radius), reference);
  const latitude1 = degreesToRadians(start.latitude);
  const latitude2 = degreesToRadians(finish.latitude);
  const longitudeDelta = degreesToRadians(finish.longitude - start.longitude);
  const y = Math.sin(longitudeDelta) * Math.cos(latitude2);
  const x = Math.cos(latitude1) * Math.sin(latitude2)
    - Math.sin(latitude1) * Math.cos(latitude2) * Math.cos(longitudeDelta);
  if (Math.abs(x) + Math.abs(y) < 1e-12) {
    return 0;
  }
  return normalizeBearing(radiansToDegrees(Math.atan2(y, x)));
}

/**
 * Averages route direction as unit vectors so camera bearing follows broad turns.
 *
 * Circular averaging prevents headings around -180 and 180 degrees from
 * incorrectly averaging toward north. The broad weighted route window reduces
 * local heading noise. Callers can select smaller physical windows for tracking.
 */
export function smoothedRouteHeading(route: RideProgressPlan, fraction: number,
  localRadius = LOCAL_HEADING_RADIUS_KILOMETERS, smoothingRadius = HEADING_SMOOTHING_RADIUS_KILOMETERS): number {
  let x = 0;
  let y = 0;
  const radius = headingFractionRadius(
    route,
    smoothingRadius,
    smoothingRadius === HEADING_SMOOTHING_RADIUS_KILOMETERS ? 0.012 : 0,
    0.12,
  );
  for (let index = 0; index < HEADING_SAMPLE_OFFSETS.length; index += 1) {
    const sample = clamp(fraction + HEADING_SAMPLE_OFFSETS[index]! * radius, 0, 1);
    const heading = degreesToRadians(routeHeading(route, sample, fraction, localRadius));
    const weight = HEADING_SAMPLE_WEIGHTS[index]!;
    x += Math.cos(heading) * weight;
    y += Math.sin(heading) * weight;
  }
  if (Math.abs(x) + Math.abs(y) < 1e-12) {
    return routeHeading(route, fraction, fraction, localRadius);
  }
  return normalizeBearing(radiansToDegrees(Math.atan2(y, x)));
}

/** Converts a physical camera-smoothing radius to a bounded route fraction. */
function headingFractionRadius(
  route: RideProgressPlan,
  radiusKilometers: number,
  minimum: number,
  maximum: number,
): number {
  if (route.totalDistanceKilometers <= 0) {
    return maximum;
  }
  return clamp(radiusKilometers / route.totalDistanceKilometers, minimum, maximum);
}

/** Normalizes an angle to the MapLibre range from -180 through 180. */
function normalizeBearing(degrees: number): number {
  return ((degrees + 180) % 360 + 360) % 360 - 180;
}

/** Numeric bounds helper. */
function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/** Converts degrees to radians. */
function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/** Converts radians to degrees. */
function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}
